import { type Virtualizer, useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef } from "react";
import { DiscrepancyCard } from "./DiscrepancyCard";
import type { DiscrepancyCardViewModel } from "./lib/view-model";

/**
 * Список карточек Discrepancy с виртуальным скроллом (T047, FR-024, R1).
 *
 * Использует `@tanstack/react-virtual` напрямую (без абстракции — R1):
 *   - `estimateSize: 280` (среднее) + `measureElement` для динамической высоты;
 *   - `overscan: 5` (5 карточек выше/ниже viewport pre-rendered);
 *   - При N<50 — plain `map()` без virtualizer (FR-024).
 *
 * Через `virtualizerRef` родитель может звать `scrollToIndex(idx, ...)` для
 * программного scroll к drill-down target (R2). Также применяется fallback
 * к нативному `scrollIntoView` для не-виртуализованного случая.
 */
const VIRTUAL_THRESHOLD = 50;
const ESTIMATE_SIZE_PX = 280;
const OVERSCAN = 5;

export interface DiscrepancyVirtualListProps {
	cards: DiscrepancyCardViewModel[];
	highlightedDiscrepancyId: string | null;
	/** Ref для программного scroll к индексу. */
	virtualizerRef: React.MutableRefObject<Virtualizer<HTMLDivElement, Element> | null>;
	/** Optional обработчик клика на drill-down ссылку внутри карточки. */
	onRelatedClick?: (targetId: string) => void;
	/** ID прогона — пробрасывается в карточки для persist localStorage-ключа (фича 024). */
	runId?: string;
}

export function DiscrepancyVirtualList({
	cards,
	highlightedDiscrepancyId,
	virtualizerRef,
	onRelatedClick,
	runId,
}: DiscrepancyVirtualListProps) {
	const parentRef = useRef<HTMLDivElement>(null);
	const useVirtual = cards.length >= VIRTUAL_THRESHOLD;

	const virtualizer = useVirtualizer({
		count: cards.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => ESTIMATE_SIZE_PX,
		overscan: OVERSCAN,
		// Sprint 5 / B3 (T-B3.3): идентификация строго по id. Раньше был
		// fallback `?? idx-${index}` — но cards[index] всегда определён при
		// валидном index. Удаление fallback гарантирует, что virtualizer
		// не использует index-based ключи (которые ломают drill-down при
		// фильтрации).
		getItemKey: (index) => String(cards[index].id),
	});

	// Передаём virtualizer наверх для программного scroll-to.
	// Sprint 5 / B3 (T-B3.3): не обнуляем ref в cleanup при перемонтировании
	// эффекта (effect re-run при изменении useVirtual/virtualizer). Раньше
	// cleanup нулял ref → если родитель в этот момент вызывал scroll-to,
	// получал null и drill-down терялся. Сейчас ref всегда указывает на
	// актуальный virtualizer (или null если не виртуализуем).
	useEffect(() => {
		virtualizerRef.current = useVirtual ? virtualizer : null;
	}, [virtualizer, useVirtual, virtualizerRef]);

	if (!useVirtual) {
		// Plain рендер для небольших списков (N<50, FR-024).
		return (
			<ul className="space-y-3">
				{cards.map((card) => (
					<li key={card.id} data-discrepancy-id={card.id}>
						<DiscrepancyCard
							viewModel={card}
							highlighted={highlightedDiscrepancyId === card.id}
							onRelatedClick={onRelatedClick}
							runId={runId}
						/>
					</li>
				))}
			</ul>
		);
	}

	const virtualItems = virtualizer.getVirtualItems();
	const totalSize = virtualizer.getTotalSize();

	return (
		<div ref={parentRef} className="max-h-[calc(100vh-16rem)] overflow-y-auto rounded-lg">
			<div style={{ height: `${totalSize}px`, width: "100%", position: "relative" }}>
				{virtualItems.map((virtualItem) => {
					const card = cards[virtualItem.index];
					return (
						<div
							key={card.id}
							data-discrepancy-id={card.id}
							ref={virtualizer.measureElement}
							data-index={virtualItem.index}
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								width: "100%",
								transform: `translateY(${virtualItem.start}px)`,
								padding: "0 0 12px 0",
							}}
						>
							<DiscrepancyCard
								viewModel={card}
								highlighted={highlightedDiscrepancyId === card.id}
								onRelatedClick={onRelatedClick}
								runId={runId}
							/>
						</div>
					);
				})}
			</div>
		</div>
	);
}
